import type { ProductVariant } from '~/graphql';
import type { ProductInterface } from '~/routes/$language.pricing.$slug/route';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type EcommerceData = {
  cart_id?: string;
  currency: string; // Currency of the items associated with the event, in 3-letter ISO 4217 format.
  transaction_id?: string; // The unique identifier of a transaction.
  value: number; // The monetary value of the event.
  coupon?: string; // The coupon name/code associated with the event.
  shipping?: number; // Shipping cost associated with a transaction.
  shipping_tier?: string; // Shipping method
  payment_type?: string; // Payment type
  tax?: number; // Tax cost associated with a transaction.
  items: Array<EcommerceItem>; // The items for the event.
  customer_type?: string;
};

// From https://developers.google.com/analytics/devguides/collection/ga4/reference/events
export type EcommerceEvent =
  | 'add_payment_info'
  | 'add_shipping_info'
  | 'add_shipping_method'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'begin_checkout'
  | 'earn_virtual_currency'
  | 'generate_lead'
  | 'join_group'
  | 'level_end'
  | 'level_start'
  | 'level_up'
  | 'login'
  | 'post_score'
  | 'purchase'
  | 'refund'
  | 'remove_from_cart'
  | 'search'
  | 'select_content'
  | 'select_item'
  | 'select_promotion'
  | 'share'
  | 'sign_up'
  | 'spend_virtual_currency'
  | 'tutorial_begin'
  | 'tutorial_complete'
  | 'unlock_achievement'
  | 'view_cart'
  | 'view_item'
  | 'view_item_list'
  | 'view_promotion'
  | 'review_order';

type EcommerceItem = {
  item_id: string | null; // The ID of the item.
  item_name: string | null; // The name of the item.
  item_version?: string | null; // The version of the item, e.g. "Full version".
  affiliation?: string | null; // A product affiliation to designate a supplying company or brick and mortar store location.
  coupon?: string | null; // The coupon name/code associated with the item.
  discount?: number | null; // The monetary discount value associated with the item.
  index?: number | null; // The index/position of the item in a list.
  item_brand?: string | null; // The brand of the item.
  item_variant?: string | null; // The item variant or unique code or description for additional item details/options.
  location_id?: string | null; // The physical location associated with the item (e.g. the physical store location). It's recommended to use the Google Place ID that corresponds to the associated item. A custom location ID can also be used.
  price?: number | null; // The monetary price of the item, in units of the specified currency parameter.
  quantity?: number | null; //	Item quantity
  sku_id?: string | null; // The SKU of the variant / VirtualProduct (cart product) / OrderItem.
  sku_name?: string | null; // The name of the variant / VirtualProduct (cart product) / OrderItem.
};

// Can't be imported from dotcom-core because it imports @apollo/client
// Either decouple dependency or let's wait until Apollo is gone forever
const pushDataEvent = (data: Record<string, unknown>) => {
  if (typeof window?.dataLayer !== 'undefined') {
    window?.dataLayer.push(data);
  }
};

function pushEcommerceEvent(
  event: EcommerceEvent,
  {
    // Refers to the current website where the event occurred.
    affiliation = 'native-instruments',
    // We set perpetual as default since is the most used in our case
    product_type = 'perpetual',
    transaction_id,
    cart_id,
    currency,
    value,
    tax,
    coupons,
    items,
    shipping_tier,
    shipping,
    payment_type,
    customer_type,
  }: {
    affiliation?: string;
    product_type?: string;
    transaction_id?: string;
    cart_id?: string | null;
    currency?: string | null;
    value?: number | null;
    tax?: number | null;
    coupons?: (string | undefined)[];
    items?: EcommerceItem[];
    shipping_tier?: string | null;
    shipping?: number | null;
    payment_type?: string | null;
    customer_type?: string | null;
  },
) {
  pushDataEvent({
    ecommerce: null,
  });

  const ecommerce: EcommerceData = {
    currency: currency || '',
    value: value || 0,
    items:
      items?.map((item) => {
        return {
          item_id: item.item_id || '',
          item_name: item.item_name || '',
          item_version: item?.item_version || '',
          affiliation: affiliation || '',
          item_brand: item.item_brand || '',
          sku_id: item.sku_id || '',
          sku_name: item.sku_name || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
        };
      }) || [],
  };

  if (cart_id) {
    ecommerce.cart_id = cart_id;
  }
  if (transaction_id) {
    ecommerce.transaction_id = transaction_id;
  }
  if (tax) {
    ecommerce.tax = tax;
  }
  if (coupons?.length) {
    ecommerce.coupon = coupons.join(';');
  }
  if (shipping_tier) {
    ecommerce.shipping_tier = shipping_tier;
  }
  if (shipping !== undefined && shipping !== null) {
    ecommerce.shipping = shipping;
  }
  if (payment_type) {
    ecommerce.payment_type = payment_type;
  }
  if (customer_type) {
    ecommerce.customer_type = customer_type || 'unknown';
  }

  pushDataEvent({
    event,
    product_type,
    ecommerce,
  });
}

type ProductEcommerceEventProps = {
  product: ProductInterface;
  variant?: ProductVariant | null | undefined;
  event: EcommerceEvent;
  customerType: string | null | undefined;
};

export function pushProductEcommerceEvent({
  product,
  variant,
  event,
  customerType,
}: ProductEcommerceEventProps) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';
  let item: ProductInterface | ProductVariant | undefined;

  // Other type of products without variants like BundleProducts or GiftCardProducts
  if (!variant && !isConfigurableProduct) {
    item = product;
  }

  if (variant) {
    item = variant;
  }

  return pushEcommerceEvent(event, {
    currency: item?.price?.final?.currency,
    value: item?.price?.final.value || 0,
    customer_type: customerType || 'unknown',
    items: [
      {
        // The parent product SKU and name is expected for item_id and item_name.
        item_id: product?.sku,
        item_name: product?.name,
        item_brand: product?.brand?.toLowerCase(),
        sku_id: item?.sku,
        sku_name: item?.name,
        item_version: item && 'licenseLabel' in item ? item.licenseLabel : '',
        price: item?.price?.final.value,
        quantity: 1,
      },
    ],
  });
}

export { pushDataEvent, pushEcommerceEvent };
